import type { ProductListQuery } from '@/api/handlers/resources/products/requests';
import { useProductListQuery } from '@/api/queries';

/**
 * The drafts the notice counts (bos-app#226): every draft in the catalog, not
 * the list's search or filters, so the number stays put while the owner
 * narrows the list. One row is enough: only the total is read. Archived
 * products are left out, as the list leaves them out, so the count is what
 * Show drafts lists.
 */
export const PRODUCT_DRAFT_COUNT_QUERY: Readonly<ProductListQuery> =
    Object.freeze({ page: 1, pageSize: 1, draft: true });

/** A refetch of a count already shown (rulebook §12). */
export type ProductDraftCountRefresh = 'idle' | 'pending' | 'error';

/**
 * The drafts notice's section states (rulebook §12): `ready` carries the
 * count of drafts, `empty` is a catalog with none.
 */
export type ProductDraftCountState =
    | { status: 'loading' }
    | { status: 'error'; onRetry: () => void }
    | {
          status: 'empty';
          refresh: ProductDraftCountRefresh;
          onRetry: () => void;
      }
    | {
          status: 'ready';
          data: number;
          refresh: ProductDraftCountRefresh;
          onRetry: () => void;
      };

/**
 * How many products are drafts, as a section state. A failed count is an
 * `error` with a retry that reads it again (review of bos-app#229, finding
 * 17): the list query hands a refusal back as its data, so a failure
 * replaces the count rather than sitting beside it. The product list keys
 * share a prefix, so an import, its undo and a publish (which invalidate
 * every list) refresh it too.
 */
export const useProductDraftCount = (): ProductDraftCountState => {
    const query = useProductListQuery(PRODUCT_DRAFT_COUNT_QUERY);
    const { refetch } = query;
    const onRetry = () => {
        void refetch();
    };

    const envelope = query.data;
    if (envelope === undefined) {
        return query.isError
            ? { status: 'error', onRetry }
            : { status: 'loading' };
    }
    if (!envelope.success) {
        return { status: 'error', onRetry };
    }
    const refresh: ProductDraftCountRefresh = query.isFetching
        ? 'pending'
        : query.isError
          ? 'error'
          : 'idle';
    const count = envelope.response.totalCount;
    return count === 0
        ? { status: 'empty', refresh, onRetry }
        : { status: 'ready', data: count, refresh, onRetry };
};
